import { Component, computed, ElementRef, OnChanges, OnDestroy, inject, signal, output, input, viewChild } from '@angular/core';
import type { SimpleChanges } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import RFB from '@novnc/novnc';

export enum VNC_CODE {
  CONNECTED = 'REMOTE_ACCESS.CONNECTED',
  DISCONNECTED = 'REMOTE_ACCESS.DISCONNECTED',
  SECURITY_FAILURE = 'REMOTE_ACCESS.SECURITY_FAILURE',
  CREDENTIALS_REQUIRED = 'REMOTE_ACCESS.CREDENTIALS_REQUIRED',
}

export interface VncViewerEvent {
  code: VNC_CODE;
  event: Event;
  clean?: boolean;
  message?: string;
}

function createVncEvent(code: VNC_CODE): VncViewerEvent {
  return {
    code,
    event: new Event(code),
  };
}

import { ChangeDetectionStrategy } from '@angular/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gea-hrt-vnc-viewer',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    @let _label = label() | translate;
    <div #screen class="vnc-screen"></div>
    <button class="fullscreen-toggle" type="button" [attr.aria-label]="_label" [title]="_label" (click)="toggleFullscreen()">
      {{ _label }}
    </button>
  `,
  styles: `
    :host {
      display: block;
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: #000;
    }

    .vnc-screen {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
    }

    .fullscreen-toggle {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      z-index: 2;
      min-height: 2.25rem;
      padding: 0.4375rem 0.75rem;
      border: 1px solid rgba(255, 255, 255, 0.45);
      border-radius: 4px;
      background: rgba(16, 20, 24, 0.86);
      color: #fff;
      backdrop-filter: blur(6px);
      cursor: pointer;
      font: inherit;
      font-size: 0.75rem;
      font-weight: 600;
      transition:
        background-color 160ms ease,
        border-color 160ms ease;
    }

    .fullscreen-toggle:hover {
      border-color: rgba(255, 255, 255, 0.8);
      background: rgba(16, 20, 24, 0.98);
    }

    .fullscreen-toggle:focus-visible {
      outline: 3px solid var(--geaui-primary-color, #47a7ff);
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .fullscreen-toggle {
        transition: none;
      }
    }
  `,
})
export class VncViewerComponent implements OnChanges, OnDestroy {
  readonly websocketUrl = input.required<string>();
  readonly password = input<string>();
  readonly vncEvent = output<VncViewerEvent>();

  readonly label = computed(() =>
    this.isFullscreen() ? 'REMOTE_ACCESS.VNC_VIEWER.EXIT_FULLSCREEN' : 'REMOTE_ACCESS.VNC_VIEWER.ENTER_FULLSCREEN'
  );

  private readonly screenRef = viewChild.required<ElementRef<HTMLDivElement>>('screen');

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  readonly isFullscreen = signal(false);

  private rfb?: RFB;
  private rfbTornDown = false;

  private readonly onConnect = (): void => {
    // noVNC records the container size once and only re-measures on a window resize event;
    // nudge it once now that the real framebuffer size is known, so it doesn't stay scaled to 0.
    window.dispatchEvent(new Event('resize'));

    this.vncEvent.emit(createVncEvent(VNC_CODE.CONNECTED));
  };

  private onDisconnect = (event: Event): void => {
    this.rfbTornDown = true;
    const detail = (event as CustomEvent<{ clean: boolean }>).detail;
    const message = detail?.clean ? 'REMOTE_ACCESS.CLEAN_DISCONNECT' : 'REMOTE_ACCESS.UNEXPECTED_DISCONNECT';

    this.vncEvent.emit({
      ...createVncEvent(VNC_CODE.DISCONNECTED),
      clean: detail?.clean === true,
      message,
    });
  };

  private readonly onSecurityFailure = (event: Event): void => {
    this.rfbTornDown = true;
    const detail = (event as CustomEvent<{ reason?: string }>).detail;
    this.vncEvent.emit({
      ...createVncEvent(VNC_CODE.SECURITY_FAILURE),
      message: detail?.reason ?? VNC_CODE.SECURITY_FAILURE,
    });
  };

  private readonly onCredentialsRequired = (): void => {
    this.vncEvent.emit(createVncEvent(VNC_CODE.CREDENTIALS_REQUIRED));
  };

  // The browser (or the user via Esc) can exit fullscreen without going through toggleFullscreen(),
  // so this is the single source of truth for isFullscreen and also re-triggers noVNC's resize.
  private readonly onFullscreenChange = (): void => {
    this.isFullscreen.set(document.fullscreenElement === this.elementRef.nativeElement);
    window.dispatchEvent(new Event('resize'));
  };

  constructor() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const websocketUrlChange = changes['websocketUrl'];
    const websocketUrl = websocketUrlChange?.currentValue as string | undefined;

    if (websocketUrlChange && websocketUrl && websocketUrl !== websocketUrlChange.previousValue) {
      this.connect(websocketUrl);
      return;
    }

    const password = this.password();
    if (changes['password'] && this.rfb && typeof password === 'string' && password.length > 0) {
      const websocketUrlValue = this.websocketUrl();
      if (this.rfbTornDown && websocketUrlValue) {
        this.connect(websocketUrlValue);
        return;
      }

      this.rfb.sendCredentials({ password });
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    if (document.fullscreenElement === this.elementRef.nativeElement) {
      void document.exitFullscreen();
    }
  }

  toggleFullscreen(): void {
    if (document.fullscreenElement === this.elementRef.nativeElement) {
      void document.exitFullscreen();
      return;
    }

    void this.elementRef.nativeElement.requestFullscreen();
  }

  private connect(websocketUrl: string): void {
    this.disconnect();

    this.rfb = new RFB(this.screenRef().nativeElement, websocketUrl, {
      credentials: {
        password: this.password(),
      },
    });

    this.rfb.scaleViewport = true;
    this.rfb.resizeSession = true;
    this.rfb.clipViewport = true;

    this.rfb.addEventListener('connect', this.onConnect);
    this.rfb.addEventListener('disconnect', this.onDisconnect);
    this.rfb.addEventListener('securityfailure', this.onSecurityFailure);
    this.rfb.addEventListener('credentialsrequired', this.onCredentialsRequired);
  }

  private disconnect(): void {
    const rfb = this.rfb;
    if (rfb) {
      rfb.removeEventListener('connect', this.onConnect);
      rfb.removeEventListener('disconnect', this.onDisconnect);
      rfb.removeEventListener('securityfailure', this.onSecurityFailure);
      rfb.removeEventListener('credentialsrequired', this.onCredentialsRequired);

      // Calling disconnect() after the server already tore down the session just logs a harmless noVNC warning.
      if (!this.rfbTornDown) {
        rfb.disconnect();
      }
    }
    this.rfb = undefined;
    this.rfbTornDown = false;
  }
}
